import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { IntelButtons } from './IntelButtons';
import { formatEffectDuration } from '../../utils/effectDuration';
import { buildSurveyRows } from '../../utils/intelPanelData';
import type { CrystalBallData, SpyOnMilitaryData, SurveyData } from '../../types/intel';

// Panel header backgrounds and text colors (matching mockup)
const CB_HD_BG = 'rgba(107,79,160,0.15)';
const CB_HD_FG = '#b89fe0';
const SM_HD_BG = 'rgba(74,122,90,0.15)';
const SM_HD_FG = '#8ec4a0';
const SV_HD_BG = 'rgba(122,106,64,0.15)';
const SV_HD_FG = '#c4b078';
const OFF_CLR = '#e8a04f';
const EFX_BG = 'rgba(107,79,160,0.2)';
const EFX_FG = '#c4b0e8';
const EFX_BD = 'rgba(107,79,160,0.3)';

const MONO = "'JetBrains Mono', 'Fira Code', monospace";

interface IntelExpandedRowProps {
  provinceId: string;
  provinceName: string;
  location: string;
  worldId: string;
  playerProvinceId: string;
  cbData?: CrystalBallData | null;
  somData?: SpyOnMilitaryData | null;
  svData?: SurveyData | null;
  onIntelGathered?: () => void;
  /** SF-105: disables SM/SV (thievery-based) buttons when the target is protected. */
  thieveryDisabled?: boolean;
}

export function IntelExpandedRow({
  provinceId, provinceName, location, worldId, playerProvinceId,
  cbData, somData, svData, onIntelGathered, thieveryDisabled,
}: IntelExpandedRowProps) {
  const navigate = useNavigate();
  const queryStr = `?targetId=${provinceId}&targetName=${encodeURIComponent(provinceName)}&targetLoc=${encodeURIComponent(location)}`;
  const hasAny = !!cbData || !!somData || !!svData;

  return (
    <div style={{ background: COLORS.cardAlt, padding: '10px 16px' }}>
      {!hasAny ? (
        <div style={{ color: COLORS.text.muted, fontSize: 12, textAlign: 'center', padding: 8 }}>
          No intel yet. Use buttons above.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {cbData && <CBPanel data={cbData} />}
          {somData && <SMPanel data={somData} />}
          {svData && <SVPanel data={svData} />}
          <ActionsColumn
            provinceId={provinceId}
            worldId={worldId}
            playerProvinceId={playerProvinceId}
            onIntelGathered={onIntelGathered}
            thieveryDisabled={thieveryDisabled}
            onWarRoom={() => navigate(`/attack${queryStr}`)}
            onThievery={() => navigate(`/thievery${queryStr}`)}
            onMagic={() => navigate(`/mystics${queryStr}`)}
          />
        </div>
      )}
    </div>
  );
}

// ── CB Panel ─────────────────────────────────────────────────────────────

function CBPanel({ data }: { data: CrystalBallData }) {
  const race = data.race;
  const rulerName = data.rulerName;
  const land = data.totalLand;
  const be = data.buildingEfficiency;
  const money = data.money;
  const food = data.food;
  const runes = data.runes;
  const tradeBal = data.tradeBalance;
  const peasants = data.peasants;
  const soldiers = data.soldiers;
  const offSpecs = data.offenseSpecialists;
  const defSpecs = data.defenseSpecialists;
  const elites = data.elites;
  const horses = data.warHorses;
  const prisoners = data.prisoners;
  const offense = data.offensePoints;
  const defense = data.defensePoints;
  const effects = data.activeEffects;

  return (
    <div style={{ flex: 1, background: COLORS.card, borderRadius: 6, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
      <PanelHeader bg={CB_HD_BG} fg={CB_HD_FG} title="Crystal Ball" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {/* Left column: province info + resources */}
        <div style={{ padding: '8px 10px', borderRight: `1px solid ${COLORS.border}` }}>
          {race != null && <LV label="Race" value={titleCase(race)} />}
          {rulerName != null && <LV label="Ruler" value={rulerName} />}
          {land != null && <LV label="Land" value={`${fmt(land)} acres`} mono />}
          {peasants != null && <LV label="Peasants" value={fmt(peasants)} mono />}
          {be != null && <LV label="Build Eff." value={`${Math.round(be * 100)}%`} mono />}
          <Divider />
          {money != null && <LV label="Gold" value={`${fmt(money)}gc`} color={COLORS.gold} mono />}
          {food != null && <LV label="Food" value={`${fmt(food)} bushels`} mono />}
          {runes != null && <LV label="Runes" value={fmt(runes)} color={CB_HD_FG} mono />}
          {tradeBal != null && (
            <LV
              label="Trade Bal."
              value={`${tradeBal >= 0 ? '+' : ''}${fmt(tradeBal)}gc`}
              color={tradeBal >= 0 ? COLORS.positive : '#d48060'}
              mono
            />
          )}
        </div>
        {/* Right column: military units + points */}
        <div style={{ padding: '8px 10px' }}>
          {soldiers != null && <LV label="Soldiers" value={fmt(soldiers)} mono />}
          {offSpecs != null && <LV label="Off Specs" value={fmt(offSpecs)} mono />}
          {defSpecs != null && <LV label="Def Specs" value={fmt(defSpecs)} mono />}
          {elites != null && <LV label="Elites" value={fmt(elites)} mono />}
          <LV label="Thieves" value="HIDDEN" hidden />
          <LV label="Wizards" value="HIDDEN" hidden />
          {horses != null && <LV label="War Horses" value={fmt(horses)} mono />}
          {prisoners != null && <LV label="Prisoners" value={fmt(prisoners)} mono />}
          <Divider />
          {offense != null && <LV label="Off. Points" value={fmt(offense)} color={OFF_CLR} mono />}
          {defense != null && <LV label="Def. Points" value={fmt(defense)} color={COLORS.link} mono />}
        </div>
      </div>
      {effects && effects.length > 0 && (
        <div style={{ padding: '6px 10px', borderTop: `1px solid ${COLORS.border}` }}>
          <span style={{ color: COLORS.text.tertiary, fontSize: 10 }}>Effects: </span>
          {effects.map((e, i) => (
            <EffectTag key={i} text={`${formatEffectName(e.effectType)} (${formatEffectDuration(e)})`} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── SM Panel ─────────────────────────────────────────────────────────────

function SMPanel({ data }: { data: SpyOnMilitaryData }) {
  const offME = data.offensiveME;
  const defME = data.defensiveME;
  const offense = data.offensePoints;
  const defense = data.defensePoints;
  const armyCount = data.armyCount;
  const hasArmiesOut = data.hasArmiesOut;

  const offMEStr = offME != null ? `${(offME * 100).toFixed(0)}%` : null;
  const defMEStr = defME != null ? `${(defME * 100).toFixed(0)}%` : null;
  const defMEVal = defME != null ? defME * 100 : 0;
  const armyOut = hasArmiesOut ?? ((armyCount ?? 0) > 0);

  return (
    <div style={{ minWidth: 210, background: COLORS.card, borderRadius: 6, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
      <PanelHeader bg={SM_HD_BG} fg={SM_HD_FG} title="Spy on Military" />
      <div style={{ padding: '8px 10px' }}>
        {offMEStr != null && <LV label="Off. ME" value={offMEStr} color={COLORS.positive} mono />}
        {defMEStr != null && <LV label="Def. ME" value={defMEStr} color={defMEVal > 100 ? COLORS.warning : COLORS.text.secondary} mono />}
        <Divider />
        {offense != null && <LV label="Net Off." value={fmt(offense)} color={OFF_CLR} mono />}
        {defense != null && <LV label="Net Def." value={fmt(defense)} color={COLORS.link} mono />}
        {(hasArmiesOut != null || (armyCount ?? 0) > 0) && (
          <>
            <Divider />
            <LV label="Army" value={armyOut ? 'OUT' : 'HOME'} color={armyOut ? COLORS.warning : COLORS.positive} />
          </>
        )}
      </div>
    </div>
  );
}

// ── SV Panel ─────────────────────────────────────────────────────────────

function SVPanel({ data }: { data: SurveyData }) {
  const totalAcres = data.totalLand ?? 0;
  const buildingRows = data.buildings ? buildSurveyRows(data.buildings, totalAcres) : [];

  return (
    <div style={{ minWidth: 210, maxHeight: 280, background: COLORS.card, borderRadius: 6, border: `1px solid ${COLORS.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '6px 10px', background: SV_HD_BG, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: SV_HD_FG, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Survey</span>
        {totalAcres > 0 && <span style={{ color: COLORS.text.muted, fontSize: 10, fontFamily: MONO }}>{fmt(totalAcres)} ac</span>}
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {buildingRows.map((b, j) => (
          <div key={b.key} style={{ display: 'flex', padding: '2px 10px', background: j % 2 === 0 ? 'transparent' : COLORS.cardAlt }}>
            <span style={{ color: COLORS.text.secondary, fontSize: 10, flex: 1 }}>{b.label}</span>
            <span style={{ color: COLORS.text.primary, fontSize: 10, fontFamily: MONO, width: 38, textAlign: 'right' }}>{fmt(b.count)}</span>
            <span style={{ color: COLORS.text.muted, fontSize: 9, fontFamily: MONO, width: 32, textAlign: 'right' }}>{b.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Actions Column ───────────────────────────────────────────────────────

function ActionsColumn({ provinceId, worldId, playerProvinceId, onIntelGathered, thieveryDisabled, onWarRoom, onThievery, onMagic }: {
  provinceId: string;
  worldId: string;
  playerProvinceId: string;
  onIntelGathered?: () => void;
  thieveryDisabled?: boolean;
  onWarRoom: () => void;
  onThievery: () => void;
  onMagic: () => void;
}) {
  return (
    <div style={{ minWidth: 130, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
      <IntelButtons
        targetProvinceId={provinceId}
        worldId={worldId}
        playerProvinceId={playerProvinceId}
        onIntelGathered={onIntelGathered}
        thieveryDisabled={thieveryDisabled}
      />
      <ActBtn label="War Room" bg={COLORS.btnPrimary} bgH={COLORS.btnPrimaryHover} fg="#e8ecf4" onClick={onWarRoom} />
      <ActBtn label="Thievery" bg="transparent" bgH="rgba(107,79,160,0.15)" fg={EFX_FG} bd="rgba(107,79,160,0.4)" onClick={onThievery} />
      <ActBtn label="Magic" bg="transparent" bgH="rgba(107,144,224,0.1)" fg={COLORS.link} bd="rgba(107,144,224,0.3)" onClick={onMagic} />
    </div>
  );
}

// ── Primitives ───────────────────────────────────────────────────────────

function PanelHeader({ bg, fg, title }: { bg: string; fg: string; title: string }) {
  return (
    <div style={{ padding: '6px 10px', background: bg, borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ color: fg, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</span>
    </div>
  );
}

function LV({ label, value, color, mono, hidden }: {
  label: string;
  value: string;
  color?: string;
  mono?: boolean;
  hidden?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span style={{ color: COLORS.text.tertiary, fontSize: 11 }}>{label}</span>
      <span style={{
        color: hidden ? COLORS.text.muted : (color || COLORS.text.primary),
        fontSize: 11,
        fontFamily: mono ? MONO : undefined,
        fontWeight: hidden ? 400 : 500,
        fontStyle: hidden ? 'italic' : 'normal',
      }}>
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${COLORS.border}`, margin: '3px 0' }} />;
}

function EffectTag({ text }: { text: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 5px',
      borderRadius: 3,
      fontSize: 9,
      background: EFX_BG,
      color: EFX_FG,
      marginRight: 3,
      marginTop: 2,
      border: `1px solid ${EFX_BD}`,
    }}>
      {text}
    </span>
  );
}

function ActBtn({ label, bg, bgH, fg, bd, onClick }: {
  label: string;
  bg: string;
  bgH: string;
  fg: string;
  bd?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{
        padding: '7px 10px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        background: bg,
        color: fg,
        border: bd ? `1px solid ${bd}` : 'none',
        textAlign: 'center',
        width: '100%',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = bgH; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = bg; }}
    >
      {label} →
    </button>
  );
}

// ── Formatters ───────────────────────────────────────────────────────────

function fmt(n: number): string {
  return Math.round(n).toLocaleString();
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatEffectName(effect: string): string {
  return effect.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\w/g, c => c.toUpperCase());
}
